/**
 * The Necromancer (PRD #804): the class that wins by attrition, debuff, and
 * command rather than by output.
 *
 * The declaration is deliberately unremarkable. It is the fourth class through
 * the format from PRD #792 and the second through the Mage's casting idiom:
 * the `channeling` family contributes a damage band and wears nothing, mana is
 * the kit's economy, and every mechanic lives on the catalog entries the
 * ability list names. The format needed no schema change to express it, which
 * is the seam proof PRD #804 asks the declaration to make.
 *
 * What is remarkable lives elsewhere: Animate Dead is the first consumer of
 * the kernel's summon payload, Poison Bolt and Corpse Skin are the first
 * consumers of the Poisoned status and the resist-reduction axis, and the kit
 * is the first whose abilities are worth more in an order. Corpse Skin before
 * Poison Bolt is the point, nothing enforces it, and the numbers reward it.
 *
 * Every number is first-pass and none of it is defended until the tuning PRD
 * (#799).
 */

import type { ArpgClassDef } from './types';

/** The mana cadence the dungeon runs: one point every five simulation ticks. */
const SHIPPED_MANA_REGEN_PER_TICK = 1 / 5;

export const NECROMANCER_CLASS: ArpgClassDef = {
  id: 'necromancer',
  name: 'Necromancer',
  blurb: 'A pale Hero who raises the dead and wins by rot rather than force.',
  playable: true,
  weapons: {
    // The Mage's idiom: a damage band for the fold and nothing on the weapon
    // layer. Slightly under the Mage's band, because a Necromancer's direct
    // hit is the start of its damage rather than the whole of it.
    channeling: { damage: 9, grip: 'one-handed', worn: false },
  },
  allowedEquipmentIds: new Set([
    'robe.plain',
    'mask.skullcap',
    'hat.wizard',
    'pants.long',
    'sandals.plain',
    'shoes.plain',
    'channeling',
  ]),
  curves: {
    // Sturdier than the Mage and shallower of pool: the class expects to be
    // walked at while its damage happens, and its casts are cheaper.
    maxHealth: { base: 85, perLevel: 5 },
    maxMana: { base: 80, perLevel: 6 },
    healthRegenPerTick: 0,
    manaRegenPerTick: SHIPPED_MANA_REGEN_PER_TICK * 1.25,
    critChance: 0,
    critMultiplier: 1.5,
  },
  abilities: [
    { abilityId: 'necromancer-poison-bolt', unlockLevel: 1, slot: 'right-mouse' },
    { abilityId: 'necromancer-animate-dead', unlockLevel: 3, slot: 'left-mouse' },
    { abilityId: 'necromancer-corpse-skin', unlockLevel: 5, slot: 'key-3' },
    { abilityId: 'necromancer-wither', unlockLevel: 7, slot: 'key-4' },
    { abilityId: 'necromancer-wraith-form', unlockLevel: 9, slot: 'key-5' },
  ],
  startingEquipment: {
    mainHandId: 'channeling',
    equipped: [
      // The shadow hue on the shipped robe and skullcap: a class look with no
      // new art, per the rendering-assets playbook.
      { id: 'robe.plain', layer: 'robe', hue: 1109 },
      { id: 'mask.skullcap', layer: 'headgear', hue: 1109 },
      { id: 'sandals.plain', layer: 'feet' },
    ],
  },
  balanceFloor: {
    // Poison Bolt deals 12 direct, so the 70-health placeholder falls on the
    // sixth bolt: fifty ticks of cooldown, plus wind-up and flight. The rot
    // the bolts stack makes the real fight shorter; the floor does not count
    // it, so this bound holds even if the numbers move.
    maxTicksToKillPlaceholder: 70,
    // 85 health against a twelve-damage swing is eight hits.
    minPlaceholderSwingsSurvived: 7,
    // Wither is the costliest cast, at sixteen of an eighty pool.
    minCostliestCastsFromFullMana: 4,
  },
};
